import {
  TextField,
  Button,
  Grid,
  Box,
  Typography,
  Stack,
  FormHelperText,
  FormControl,
  InputLabel,
} from "@mui/material";
import QRCode from "react-qr-code";
import { FileCopy as CopyIcon } from "@mui/icons-material";
import { AnimatePresence, motion } from "motion/react";
import QRCodePendingSkeleton from "./components/QRCodePendingSkeleton";
import useMultifactorAuthSettings from "./hooks/useMultifactorAuthSettings";
import { visuallyHidden } from "@mui/utils";

interface MfaQrCodeEnrollProps {
  state: ReturnType<typeof useMultifactorAuthSettings>["state"];
  pending: boolean;
  qrCodeUrl: string | undefined;
  manualCode: string | undefined;
  verificationCode: string;
  handleAddTotp: ReturnType<typeof useMultifactorAuthSettings>["handleAddTotp"];
  setVerificationCode: (code: string) => void;
  handleVerifyTotp: (e: React.FormEvent) => Promise<void>;
  signInMethod: string | undefined;
  user: ReturnType<typeof useMultifactorAuthSettings>["user"];
  password: ReturnType<typeof useMultifactorAuthSettings>["password"];
  setPassword: ReturnType<typeof useMultifactorAuthSettings>["setPassword"];
}

export const MfaQrCodeEnroll = ({
  state,
  pending,
  qrCodeUrl,
  manualCode,
  verificationCode,
  setVerificationCode,
  handleAddTotp,
  handleVerifyTotp,
  signInMethod,
  user,
  password,
  setPassword,
}: MfaQrCodeEnrollProps) => {
  const verifying = state.status === "pending mfa enrollment";

  const formattedManualCode = manualCode
    ? manualCode.match(/.{1,4}/g)?.join(" ")
    : "";

  const qrCodeReady = qrCodeUrl && manualCode;
  const needsLogin = signInMethod === "password" && !qrCodeReady;

  return (
    <AnimatePresence mode="wait">
      {pending && <QRCodePendingSkeleton key="pending" />}
      {needsLogin && (
        <Grid
          container
          size={12}
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            p: 2,
          }}
        >
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body1" sx={{ mt: 2 }}>
              For your security, please re-enter your password to enable MFA
              enrollment.
            </Typography>
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                handleAddTotp();
              }}
              sx={{ mt: 2, mb: 2 }}
            >
              <Stack
                direction="column"
                spacing={2}
                sx={{ minWidth: 300, mt: 2 }}
              >
                <TextField
                  disabled
                  type="email"
                  placeholder="User Email"
                  label="Email"
                  value={user?.email || ""}
                />
                <TextField
                  type="password"
                  placeholder="Password"
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  Submit
                </Button>
              </Stack>
            </Box>
          </Grid>
        </Grid>
      )}
      {qrCodeReady && (
        <Grid
          container
          size={12}
          component={motion.div}
          key="loaded"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Grid
            size="auto"
            sx={{
              display: "flex",
              flexDirection: "column",
              p: 2,
            }}
          >
            <Box
              sx={{
                p: 2,
                width: "100%",
                maxWidth: 256,
                height: 256,
                backgroundColor: "white",
                borderRadius: 2,
              }}
            >
              <QRCode
                size={256}
                viewBox={`0 0 256 256`}
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                level="H"
                value={qrCodeUrl}
              />
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
                If you can't scan the QR code above, enter this text instead
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    flexGrow: 1,
                    backgroundColor: "background.light",
                    p: 1,
                    height: 40,
                    borderRadius: 1,
                  }}
                >
                  {formattedManualCode}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    display: "flex",
                    p: 1,
                    m: 0,
                    height: 40,
                    minWidth: 40,
                    borderColor: "background.light",
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(manualCode);
                  }}
                >
                  <CopyIcon fontSize="small" />
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid size="grow" sx={{ p: 2 }}>
            <Typography>
              Scan the QR code with your authenticator app and enter the
              verification code below to complete enrollment.
            </Typography>
            <Box component="form" onSubmit={handleVerifyTotp} sx={{ mt: 3 }}>
              <Stack spacing={2} direction="row">
                <FormControl>
                  <InputLabel htmlFor="verification-code" sx={visuallyHidden}>
                    Verification Code
                  </InputLabel>
                  <TextField
                    type="text"
                    size="small"
                    autoComplete="one-time-code"
                    aria-describedby="verification-code-error"
                    placeholder="Enter code"
                    label="Verification Code"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value)}
                    error={Boolean(state.error)}
                  />
                </FormControl>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={verifying}
                  loadingPosition="start"
                  sx={{ alignSelf: "start" }}
                >
                  Verify
                </Button>
              </Stack>
              <FormHelperText
                error
                id="verification-code-error"
                aria-live="polite"
              >
                {state.error}
              </FormHelperText>
            </Box>
          </Grid>
        </Grid>
      )}
    </AnimatePresence>
  );
};
